import { Grid } from '@material-ui/core';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import ProfessionalIdentityInfo from '@/components/Common/ProfessionalIdentityInfo.tsx';
import FilledButton from '@/components/FilledButton.jsx';
import NonBookableAlert from '@/components/NonBookableAlert.jsx';

import { Context as BookAppointmentContext } from '@/context/BookAppointmentContext.js';
import { ContextProvider } from '@/context/ProjectContext.jsx';

import { BookingStep } from '@/utils/constants.js';
import { getConsentTreatments } from '@/utils/helpers.js';

import { CLEARED_CHAIN_PAGING, useBookingStore, usePersistedPrismicStore, useSearchStore } from '@/store.js';

import PatientTypeSelection from './PatientTypeSelection.jsx';

const useStyles = makeStyles((theme) => ({
    accordion: {
        background: 'transparent',
        boxShadow: 'none',
        width: '100%',
        height: 'auto !important',
    },
    accordionRoot: {
        '&.MuiAccordion-root:before': {
            backgroundColor: 'transparent',
        },
        margin: 0,
        '&.Mui-expanded': {
            margin: 0,
        },
    },
    accordionSummaryRoot: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '0.5rem 0',
    },
    accordionDetailsRoot: {
        padding: '8px 16px 16px',
        [theme.breakpoints.down('xs')]: {
            padding: '0.5rem 1.5.rem',
        },
    },
    calendarCardContent: {
        maxWidth: theme.layout.contentWidth,
        display: 'block',
        padding: '30px 30px 60px 30px',
        borderLeft: theme.border.light,
    },
    cardContentClasses: {
        position: 'relative',
        display: 'block',
        paddingTop: 20,
        paddingBottom: 20,
    },
    professionalColumn: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    identityInfo: {
        width: '100%',
        padding: '10px 20px 0',
        boxSizing: 'border-box',
    },
    seeProfileWrap: {
        width: '-webkit-fill-available',
        paddingLeft: 20,
        paddingRight: 20,
        marginTop: 20,
    },
    accordionImg: {
        width: '3.5rem',
        height: '3.5rem',
        borderRadius: '50%',
        objectFit: 'cover',
        border: theme.border.light,
    },
    selectedCard: {
        border: '2px solid #000',
    },
}));

const ProfessionalResult = ({ doctor, selectedDoctor, mainData, dataSet, promptOverride, isSelected = false }) => {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('xs'));
    const selectedTreatments = useSearchStore((state) => state.selectedTreatments);
    const treatments = useSearchStore((state) => state.treatments);
    const selectedLocation = useSearchStore((state) => state.selectedLocation);
    const allDocuments = useSearchStore((state) => state.allDocuments);
    const selectedHealthInsurance = useSearchStore((state) => state.selectedHealthInsurance);
    const selectedChainConfig = useSearchStore((state) => state.selectedChainConfig);
    const [isExpanded, setIsExpanded] = useState(false);

    const history = useHistory();
    const {
        state: { currentDoctor },
    } = useContext(BookAppointmentContext);

    const { setMyAppointmentsData, setMyAppointmentsDataStatus, setIsDoctorsPage } = useContext(ContextProvider);

    const {
        [i18n.language]: { professionalProfiles },
    } = usePersistedPrismicStore((state) => state.findAppointmentData);

    const professions = useMemo(() => {
        if (!doctor) return [];
        // Chain professionals carry no `specialityType`; drop falsy entries for the length-0 fallback.
        return [doctor.specialityType].filter(Boolean);
    }, [doctor]);

    // Chain professionals carry only identity + gating flags; backfill display fields from the
    // matching Prismic profile, keeping the professional's own values when present.
    const enrichedDoctor = useMemo(() => {
        if (!doctor) return doctor;

        const needsEnrichment =
            !doctor.profile_picture?.url || !doctor.languages?.length || !doctor.practice_name?.[0]?.text;
        if (!needsEnrichment) return doctor;

        const prismicProfile = professionalProfiles?.find(
            (profile) => profile.key?.[0]?.text === doctor.key?.[0]?.text
        );
        if (!prismicProfile) return doctor;

        return {
            ...doctor,
            // Never overwrite a present value with a missing Prismic field; the render dereferences these.
            profile_picture: doctor.profile_picture?.url
                ? doctor.profile_picture
                : (prismicProfile.profile_picture ?? doctor.profile_picture),
            languages: doctor.languages?.length ? doctor.languages : (prismicProfile.languages ?? doctor.languages),
            practice_name: doctor.practice_name?.[0]?.text
                ? doctor.practice_name
                : (prismicProfile.practice_name ?? doctor.practice_name),
        };
    }, [doctor, professionalProfiles]);

    const goToProfile = (doc) => {
        const uniqueKey = doc.key[0].text;
        const data = [];
        const foundProfile = professionalProfiles.find((pp) => pp.key[0].text === uniqueKey);
        data['en-us'] = foundProfile;
        data['de-de'] = foundProfile;

        setMyAppointmentsData(data);
        setMyAppointmentsDataStatus(dataSet);
        setIsDoctorsPage(true);
        history.push(`/professional/${uniqueKey}`);
    };

    const startBooking = (isReturningPatient) => {
        if (selectedChainConfig) {
            useSearchStore.setState({
                chainIsReturningPatient: isReturningPatient ?? false,
                chainMainPractitionerCandidate: doctor,
                chainMainPractitioner: { type: 'specific', id: doctor.practitioner_id ?? doctor.id },
                ...CLEARED_CHAIN_PAGING,
            });
            useBookingStore.setState({
                showBookingModal: true,
                currentStep: BookingStep.CHAIN_SUGGESTIONS,
                isChainSuggestionFetching: false,
            });
            return;
        }

        const availableProfessional = useSearchStore
            .getState()
            .availableProfessionals.find((ap) => ap.key[0].text === doctor.key[0].text);

        const targetId =
            availableProfessional?.dc_calendar_ids?.[0] ?? availableProfessional?.data_cur_resource_ids?.[0];
        const pvsProvider = availableProfessional?.pvs_provider;

        const consentTreatments = getConsentTreatments({
            selectedTreatments: selectedTreatments,
            treatments: treatments,
            selectedLocation: selectedLocation,
            allDocuments: allDocuments,
            selectedHealthInsurance: selectedHealthInsurance,
        });

        const determineBookingStep = () => {
            if (consentTreatments.length > 0) {
                return BookingStep.CONSENT;
            }
            return BookingStep.TIMESLOT_SELECTION;
        };

        const { isOnlineAppointment } = useSearchStore.getState();
        useBookingStore.getState().resetStore();
        useBookingStore.setState({
            isReturningPatient: isReturningPatient ?? false,
            isOnlineAppointment: isOnlineAppointment,
            showReturningPatientToggle: false,
            showPatientTypeSelection: false,
            selectedProfessional: doctor,
            professionalDetails: availableProfessional,
            showBookingModal: true,
            consentTreatments: consentTreatments,
            currentStep: determineBookingStep(),
            pvsProvider: pvsProvider,
            targetId: targetId,
            isProfessionalSelectionSkipped: false,
        });
    };

    if (selectedDoctor && doctor.id !== selectedDoctor.id) {
        return null;
    }

    // Chain cards (promptOverride present) use the app-local i18n profile label; single-appointment
    // keeps its Prismic-sourced CTA (or the legacy fallback before Prismic data arrives).
    const singleAppointmentSeeProfileLabel = mainData?.see_profile_cta?.[0]?.text || t('SeeProfile');
    const seeProfileLabel = promptOverride ? t('ChainSeeProfile') : singleAppointmentSeeProfileLabel;

    const activePicture = currentDoctor[0]?.profile_picture?.url
        ? currentDoctor[0].profile_picture
        : enrichedDoctor?.profile_picture;
    const displayName = currentDoctor[0]?.display_name?.[0]?.text ?? doctor.display_name[0].text;
    const displayLanguages = (currentDoctor.length > 0 ? currentDoctor : [enrichedDoctor])[0].languages;
    const displayPracticeName = currentDoctor[0]?.practice_name?.[0]?.text ?? enrichedDoctor.practice_name?.[0]?.text;
    const professionText =
        professions.length === 0
            ? (doctor?.specialityType?.name?.[0]?.text ?? 'Meditation Trainer')
            : professions.map((profession) => profession.name[0].text).join(' / ');

    const renderProfessionalCard = () => (
        <div className={classes.professionalColumn}>
            <ProfessionalIdentityInfo
                variant="card"
                className={classes.identityInfo}
                imageUrl={activePicture?.url ?? null}
                imageAlt={activePicture?.alt}
                name={displayName}
                professionText={professionText}
                practiceName={displayPracticeName}
                languages={displayLanguages}
            />
            <div className={classes.seeProfileWrap}>
                <FilledButton text={seeProfileLabel} onPress={() => goToProfile(doctor)} fullWidth />
            </div>
        </div>
    );

    if (matchesXs) {
        return (
            <div
                className={clsx(isSelected && classes.selectedCard)}
                style={{
                    backgroundColor: 'white',
                    maxWidth: theme.layout.contentWidth,
                    display: 'block',
                }}
            >
                <Accordion
                    className={classes.accordion}
                    classes={{ root: classes.accordionRoot }}
                    elevation={0}
                    expanded={isExpanded}
                    onChange={() => setIsExpanded(!isExpanded)}
                >
                    <AccordionSummary
                        classes={{
                            content: classes.accordionSummaryRoot,
                        }}
                        style={{
                            borderBottom: !isExpanded ? '0.5px solid #ccc' : 0,
                        }}
                        expandIcon={<ExpandMoreIcon />}
                    >
                        {!isExpanded && (
                            <img src={activePicture?.url} className={classes.accordionImg} alt={activePicture?.alt} />
                        )}
                        <Typography style={{ display: 'flex', alignItems: 'center' }}>{displayName}</Typography>
                    </AccordionSummary>
                    <AccordionDetails
                        classes={{
                            root: classes.accordionDetailsRoot,
                        }}
                        style={{
                            borderBottom: isExpanded ? '0.5px solid #ccc' : 0,
                        }}
                    >
                        {renderProfessionalCard()}
                    </AccordionDetails>
                </Accordion>
                <div
                    style={{
                        padding: '0 16px',
                    }}
                >
                    {doctor?.is_bookable_by_patient ? (
                        <PatientTypeSelection
                            isInAccordion
                            doctor={doctor}
                            setPatientType={startBooking}
                            mainData={mainData}
                            promptOverride={promptOverride}
                            loading={!dataSet}
                        />
                    ) : (
                        <NonBookableAlert />
                    )}
                </div>
            </div>
        );
    }

    return (
        <Grid
            container
            spacing={2}
            style={{
                marginTop: 15,
            }}
        >
            <Grid item md={4} sm={5} xs={12}>
                <div className={clsx(classes.cardContentClasses, isSelected && classes.selectedCard)}>
                    {renderProfessionalCard()}
                </div>
            </Grid>

            <Grid item md={8} sm={7} xs={12}>
                <div
                    className={classes.calendarCardContent}
                    style={{
                        padding: 30,
                    }}
                >
                    {doctor?.is_bookable_by_patient ? (
                        <PatientTypeSelection
                            doctor={doctor}
                            setPatientType={startBooking}
                            mainData={mainData}
                            promptOverride={promptOverride}
                            loading={!dataSet}
                        />
                    ) : (
                        <NonBookableAlert />
                    )}
                </div>
            </Grid>
        </Grid>
    );
};

export default ProfessionalResult;
